import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import PageTitle from "../../layouts/PageTitle";
import { cwGetWorkerCalendar } from "../../../services/contractWorkers";

// ─────────────────────────────────────────────────────────────────────────────
// Contract Workers → Worker Calendar
// Month grid for a single worker: green = working day (present), red = absent,
// grey = upcoming. The summary panel shows the total working-days count for the
// month; clicking a working day shows that day's group photo.
// ─────────────────────────────────────────────────────────────────────────────

const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:5001";
const resolveUrl = (u) =>
  !u ? null : u.startsWith("http") ? u : `${API_ORIGIN}${u}`;

const TAG_COLOR = {
  PRESENT: "#2bc155",
  HALF_DAY: "#f6a821",
  LEAVE: "#3f9ae0",
  ABSENT: "#f32d2d",
  UPCOMING: "#64748b",
};
const TAG_LABEL = {
  PRESENT: "Full Day",
  HALF_DAY: "Half Day",
  LEAVE: "Leave",
  ABSENT: "Absent",
  UPCOMING: "Upcoming",
};
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const ContractWorkerCalendar = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [monthDate, setMonthDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const monthStr = `${monthDate.getFullYear()}-${String(
    monthDate.getMonth() + 1,
  ).padStart(2, "0")}`;

  useEffect(() => {
    setLoading(true);
    setSelected(null);
    cwGetWorkerCalendar(id, monthStr)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id, monthStr]);

  const days = data?.days || [];
  const firstDow = new Date(
    Date.UTC(monthDate.getFullYear(), monthDate.getMonth(), 1),
  ).getUTCDay();
  const blanks = Array(firstDow).fill(null);
  const sel = selected && days.find((d) => d.date === selected);

  const shift = (delta) => {
    const d = new Date(monthDate);
    d.setMonth(d.getMonth() + delta);
    setMonthDate(d);
  };

  return (
    <>
      <PageTitle activeMenu="Worker Calendar" motherMenu="Contract Workers" />
      <div className="row">
        <div className="col-xl-8">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => shift(-1)}
              >
                &lsaquo;
              </button>
              <div className="text-center">
                <h5 className="mb-0">
                  {monthDate.toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  })}
                </h5>
                {data?.worker && (
                  <small className="text-muted text-capitalize">
                    {data.worker.name} ·{" "}
                    {data.worker.project?.projectName || "—"}
                  </small>
                )}
              </div>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => shift(1)}
              >
                &rsaquo;
              </button>
            </div>
            <div className="card-body p-2">
              {loading ? (
                <div className="text-center py-4">Loading…</div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7,1fr)",
                    gap: 4,
                  }}
                >
                  {DOW.map((d) => (
                    <div
                      key={d}
                      className="text-center fw-bold small text-muted py-1"
                    >
                      {d}
                    </div>
                  ))}
                  {blanks.map((_, i) => (
                    <div key={`b${i}`} />
                  ))}
                  {days.map((day) => {
                    const color = TAG_COLOR[day.tag] || "#ccc";
                    return (
                      <div
                        key={day.date}
                        onClick={() =>
                          setSelected(
                            day.date === selected ? null : day.date,
                          )
                        }
                        style={{
                          background: `${color}22`,
                          borderLeft: `3px solid ${color}`,
                          borderRadius: 4,
                          padding: "6px 4px",
                          cursor: "pointer",
                          minHeight: 54,
                          textAlign: "center",
                          outline:
                            selected === day.date
                              ? `2px solid ${color}`
                              : "none",
                        }}
                      >
                        <div className="fw-bold" style={{ fontSize: 13 }}>
                          {parseInt(day.date.split("-")[2], 10)}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            color,
                          }}
                        >
                          {TAG_LABEL[day.tag]}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="card-footer d-flex gap-3">
              {Object.entries(TAG_LABEL).map(([tag, label]) => (
                <span
                  key={tag}
                  className="d-flex align-items-center gap-1 small"
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      background: TAG_COLOR[tag],
                      borderRadius: 2,
                      display: "inline-block",
                    }}
                  />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Summary</h5>
            </div>
            <div className="card-body">
              <h2 className="mb-0">{data?.summary?.workingDays ?? 0}</h2>
              <p className="text-muted mb-2">
                Working days in{" "}
                {monthDate.toLocaleString("default", { month: "long" })}{" "}
                <span className="small">
                  (half day = 0.5)
                </span>
              </p>
              <div className="d-flex flex-wrap gap-3 small">
                <span>
                  <strong>{data?.summary?.present ?? 0}</strong> full
                </span>
                <span>
                  <strong>{data?.summary?.halfDays ?? 0}</strong> half
                </span>
                <span>
                  <strong>{data?.summary?.leaveDays ?? 0}</strong> leave
                </span>
                <span>
                  <strong>{data?.summary?.absentDays ?? 0}</strong> absent
                </span>
              </div>

              {sel && (
                <div className="mt-3">
                  <h6>{sel.date}</h6>
                  <span
                    className="badge"
                    style={{ background: TAG_COLOR[sel.tag] }}
                  >
                    {TAG_LABEL[sel.tag]}
                  </span>
                  {sel.groupPhotoUrl && (
                    <div className="mt-2">
                      <img
                        src={resolveUrl(sel.groupPhotoUrl)}
                        alt="group"
                        style={{ width: "100%", borderRadius: 8 }}
                      />
                      <small className="text-muted">
                        Group photo for this day
                      </small>
                    </div>
                  )}
                </div>
              )}

              <button
                className="btn btn-link p-0 mt-3"
                onClick={() => navigate("/contract-workers/roster")}
              >
                &larr; Back to roster
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContractWorkerCalendar;
